import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { DEBOUNCE_MS } from '@/constants/timing';
import { useLocalization } from '@/hooks/useLocalization';
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';

interface SearchBarProps {
  /** Unique identifier for the search input field */
  id: string;
  /** Label text displayed for the search input */
  label: string;
  /**
   * Minimum number of characters required before triggering the debounced onSearch callback.
   * @default 0
   */
  minCharacters?: number;
  /**
   * Debounced search callback triggered after DEBOUNCE_MS.SEARCH of user inactivity.
   * Use this for server-side search requests or expensive operations.
   * The search value is automatically trimmed before being passed to this callback.
   */
  onSearch: (search: string) => void;
  /**
   * Immediate, non-debounced callback triggered on every keystroke.
   * Use this for instant client-side filtering or real-time UI updates.
   * The raw search value (not trimmed) is passed to this callback.
   */
  onChange?: (search: string) => void;
  /**
   * Whether the search bar should start in a collapsed state (mobile-friendly).
   * When true, shows only a search icon button that expands to full search bar when clicked.
   * @default false
   */
  expandable?: boolean;
  /** Force the label to shrink to avoid clipping in dense layouts */
  shrinkLabel?: boolean;
  /** Whether the input should take the full width of its container (defaults to true) */
  fullWidth?: boolean;
  /** Disables the input (e.g. while a parent operation is in progress) */
  disabled?: boolean;
}

/** Methods exposed by the SearchBar component ref for programmatic control */
export interface SearchBarHandle {
  /** Clears the search input and triggers both onChange and onSearch callbacks with empty string */
  clearSearch: () => void;
  /** Programmatically sets the search value and triggers both onChange and onSearch callbacks */
  setSearch: (search: string) => void;
}

/**
 * A reusable search bar component with support for both immediate and debounced search callbacks.
 *
 * Features:
 * - Immediate onChange for client-side filtering
 * - Debounced onSearch for server requests (DEBOUNCE_MS.SEARCH delay), or at once on Enter
 * - Optional expandable mode for mobile-friendly UI
 * - Programmatic control via ref methods
 * - Built-in clear functionality
 *
 * @example
 * ```tsx
 * const SearchExample = () => {
 *   const searchRef = useRef<SearchBarHandle>(null);
 *
 *   const handleImmediate = (value: string) => {
 *     // Instant client-side filtering
 *     setFilteredItems(items.filter(item => item.name.includes(value)));
 *   };
 *
 *   const handleDebounced = (value: string) => {
 *     // Server search after DEBOUNCE_MS.SEARCH delay
 *     searchAPI(value);
 *   };
 *
 *   return (
 *     <SearchBar
 *       ref={searchRef}
 *       id="item-search"
 *       label="Search items"
 *       onChange={handleImmediate}
 *       onSearch={handleDebounced}
 *     />
 *   );
 * };
 * ```
 */
export const SearchBar = forwardRef<SearchBarHandle, SearchBarProps>(
  (props, ref) => {
    const {
      id,
      label,
      onSearch,
      onChange,
      minCharacters = 0,
      expandable = false,
      fullWidth = true,
      disabled = false,
    } = props;
    const { t } = useLocalization();
    const searchLabel = t('grid.search', 'Search');
    const clearLabel = t('clear', 'Clear');
    const [stateValue, setStateValue] = useState('');
    const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastSearchRef = useRef('');

    const runSearch = useCallback(
      (term: string) => {
        lastSearchRef.current = term;
        onSearch(term);
      },
      [onSearch],
    );

    const cancelPendingSearch = useCallback(() => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    }, []);

    useEffect(() => cancelPendingSearch, [cancelPendingSearch]);
    const [isExpanded, setIsExpanded] = useState(!expandable);
    const [wasExpandable, setWasExpandable] = useState(expandable);
    if (expandable !== wasExpandable) {
      setWasExpandable(expandable);
      // Text in the box means an active search, so the field stays open.
      if (expandable && stateValue === '') setIsExpanded(false);
    }

    const clearSearch = useCallback(() => {
      setStateValue('');

      runSearch('');
      if (onChange) {
        onChange('');
      }
      cancelPendingSearch();
      if (expandable) {
        setIsExpanded(false);
      }
    }, [
      runSearch,
      onChange,
      cancelPendingSearch,
      setStateValue,
      expandable,
      setIsExpanded,
    ]);

    const handleSearch = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        // Call immediate onChange if provided
        if (onChange) {
          onChange(value);
        }

        // Before the threshold check, so shrinking the input below
        // minCharacters also drops the search scheduled for the longer value.
        cancelPendingSearch();

        if (value.length >= minCharacters) {
          searchTimerRef.current = setTimeout(() => {
            runSearch(value.trim());
          }, DEBOUNCE_MS.SEARCH);
        }

        setStateValue(value);
      },
      [minCharacters, runSearch, onChange, cancelPendingSearch, setStateValue],
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
        const term = stateValue.trim();
        if (stateValue.length < minCharacters) return;
        if (term === lastSearchRef.current) return;
        cancelPendingSearch();
        runSearch(term);
      },
      [cancelPendingSearch, minCharacters, runSearch, stateValue],
    );

    // Expose the clearSearch function via useImperativeHandle
    useImperativeHandle(ref, () => ({
      clearSearch,
      setSearch: (search: string) => {
        cancelPendingSearch();
        setStateValue(search);
        runSearch(search);
        if (onChange) {
          onChange(search);
        }
      },
    }));

    if (expandable && !isExpanded) {
      return (
        <Tooltip title={searchLabel}>
          <IconButton
            name='search-action'
            onClick={() => {
              setIsExpanded(true);
            }}
            sx={{
              mt: 2,
              mb: 2,
              borderColor: 'primary.main',
              border: '1px solid',
              borderRadius: '8px',
              padding: '12px', // Adjusted padding to match specs
              height: '48px', // Set fixed height to match specs
              width: '48px', // Set width to match height for square button
              '& .MuiSvgIcon-root': {
                fontSize: '24px', // Set icon size to match line height spec
              },
            }}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <TextField
        id={id}
        label={label}
        variant='outlined'
        fullWidth={fullWidth}
        disabled={disabled}
        value={stateValue}
        onChange={handleSearch}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position='end'>
                {stateValue.length > 0 ? (
                  <Tooltip title={clearLabel}>
                    <IconButton
                      name='clear-action'
                      onClick={() => {
                        clearSearch();
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title={searchLabel}>
                    <IconButton
                      onClick={() => {
                        if (expandable) {
                          setIsExpanded(false);
                        }
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
    );
  },
);

// Ensure the component has a display name for React DevTools
SearchBar.displayName = 'SearchBar';
